import React, { useEffect, useMemo, useState } from 'react';
import Base from '../Base';
import Title from '../../components/common/Title';
import { useTranslation } from 'react-i18next';
import { fetchUserInfo, teamPhoto } from '../../utils';
import Card from '../../components/elements/Card';
import Image from '../../components/images/Image';
import Center from '../../components/css/Center';
import SameLine from '../../components/css/SameLine';
import Icon from '../../components/common/Icon';
import Back from '../../components/common/Back';

const Page = () => {
  const { t, i18n } = useTranslation();
  const [team, setTeam] = useState(null);
  const [cheersData, setCheersData] = useState(null);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('newest');
  const [, setLoading] = useState(true);
  const [, setError] = useState(null);

  useEffect(() => {
    fetchUserInfo('/team', setTeam, setLoading, setError, ['otherTeamId']);
    fetchUserInfo('/cheers', setCheersData, setLoading, setError, ['otherTeamId']);
  }, []);

  const cheers = cheersData?.cheers || [];
  const totalVotes = team?.cheer_count ?? cheers.length;
  const teamName = team?.name;
  const teamPicture = teamPhoto(team?.picture, team?.company_picture);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = cheers;
    if (q) {
      list = list.filter(c =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.message || '').toLowerCase().includes(q)
      );
    }
    const sorted = [...list];
    if (sort === 'newest') {
      sorted.sort((a, b) => new Date(b.post_date) - new Date(a.post_date));
    } else if (sort === 'oldest') {
      sorted.sort((a, b) => new Date(a.post_date) - new Date(b.post_date));
    } else if (sort === 'name') {
      sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }
    return sorted;
  }, [cheers, query, sort]);

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' });
    } catch {
      return '';
    }
  };

  return (
    <Base activeNav="home">
      {/* Header card with team + total. The arrow lives INSIDE it: dropped above the card it
          landed on the card's top-left corner, half of it over the page margin and half under
          the card itself. */}
      <Card p="3" className="text-center">
        <Back />
        <Center>
          <Image url={teamPicture} size="5rem" className="circle mb-3" reload />
        </Center>
        <div className="text-secondary text-uppercase fs-7" style={{ letterSpacing: '0.1em' }}>
          {t('allVotes')}
        </div>
        <div style={{ fontSize: '2.5rem', fontWeight: 800, lineHeight: 1, color: 'var(--primary-color)' }}>
          {totalVotes}
        </div>
        {teamName && <div className="text-muted mt-1">{teamName}</div>}
      </Card>

      {/* Search + sort */}
      <Card p="3">
        <div className="mb-2">
          <input
            type="text"
            className="form-control"
            placeholder={t('searchVotes')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="d-flex gap-2 flex-wrap">
          {[
            { key: 'newest', label: t('newest') || 'Newest' },
            { key: 'oldest', label: t('oldest') || 'Oldest' },
            { key: 'name',   label: t('byName') || 'A → Z' },
          ].map(opt => (
            <button
              key={opt.key}
              type="button"
              className={`btn btn-sm ${sort === opt.key ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setSort(opt.key)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </Card>

      {/* List */}
      {filtered.length === 0 ? (
        <Card p="3">
          <Center>
            <Image url={require('./../../assets/images/not-found.png')} className="my-3" />
          </Center>
          <Title heading="5" my="1" className="text-center">
            {query ? (t('noResultsFound') || 'No results') : t('noVotesYet')}
          </Title>
        </Card>
      ) : (
        filtered.map(cheer => (
          <Card
            key={cheer.id}
            mx="2"
            mb="2"
            p="3"
            to={`/u/${cheer.user_id}`}
          >
            <div className="d-flex align-items-start">
              <Image url={cheer.photo_url || undefined} size="2.5rem" className="circle me-3" reload />
              <div style={{ textAlign: 'left', flex: 1, minWidth: 0 }}>
                <SameLine>
                  <div style={{ fontWeight: 600 }}>{cheer.name}</div>
                  <span className="text-secondary fs-7 ms-auto">{formatDate(cheer.post_date)}</span>
                </SameLine>
                <div className="text-secondary mt-1" style={{ wordBreak: 'break-word' }}>
                  {cheer.message}
                </div>
              </div>
            </div>
          </Card>
        ))
      )}

      {filtered.length > 0 && (
        <div className="text-center text-secondary fs-7 my-3">
          <Icon name="check" className="me-1" />
          {filtered.length} / {totalVotes}
        </div>
      )}
    </Base>
  );
};

export default Page;
